import type { SxProps, Theme } from '@mui/material/styles'
import { alpha } from '@mui/material/styles'

/** Outer “Platform Overview” paper on the admin dashboard. */
export function adminOverviewPaperSx(theme: Theme): SxProps<Theme> {
  return {
    background: theme.palette.background.paper,
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '32px',
    boxShadow: theme.shadows[2],
    border: `1px solid ${theme.palette.divider}`,
  }
}

/** Status row inside platform overview (muted strip). */
export function adminOverviewStatusBarSx(theme: Theme): SxProps<Theme> {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    background:
      theme.palette.mode === 'dark'
        ? theme.palette.action.hover
        : theme.palette.grey[100],
    borderRadius: '8px',
    marginBottom: '24px',
    border: `1px solid ${theme.palette.divider}`,
  }
}

/** Small metric tile inside the overview grid. */
export function adminMetricTileSx(theme: Theme, displayPrimaryColor: string): SxProps<Theme> {
  return {
    padding: '20px',
    background: theme.palette.background.paper,
    borderRadius: '12px',
    border: `2px solid ${theme.palette.divider}`,
    textAlign: 'center',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: displayPrimaryColor,
      boxShadow: `0 4px 12px 0 ${alpha(displayPrimaryColor, 0.15)}`,
    },
  }
}

/** Shared admin tool card surface (gradient bar and hover stay per-card). */
export function adminToolCardBaseSx(theme: Theme): SxProps<Theme> {
  return {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '16px',
    padding: '28px',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: theme.shadows[1],
  }
}

const adminToolTopBar = {
  content: '""',
  position: 'absolute' as const,
  top: 0,
  left: 0,
  right: 0,
  height: '3px',
  transform: 'scaleX(0)',
  transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  transformOrigin: 'left' as const,
}

/** Top accent + hover elevation for admin dashboard tool cards (brand primary). */
export function adminToolCardChromeSx(theme: Theme, displayPrimaryColor: string): SxProps<Theme> {
  const p = displayPrimaryColor
  return {
    '&::before': {
      ...adminToolTopBar,
      background: `linear-gradient(135deg, ${p} 0%, ${theme.palette.primary.light} 100%)`,
    },
    '&:hover': {
      transform: 'translateY(-6px)',
      boxShadow: `0 12px 28px 0 ${alpha(p, 0.15)}`,
      borderColor: alpha(p, 0.2),
      '&::before': { transform: 'scaleX(1)' },
    },
  }
}

/** Base + chrome for standard admin tool cards (avoids fragile `sx` spread typing). */
export function adminToolCardFullSx(theme: Theme, displayPrimaryColor: string): SxProps<Theme> {
  return {
    ...adminToolCardBaseSx(theme),
    ...adminToolCardChromeSx(theme, displayPrimaryColor),
  } as SxProps<Theme>
}

/** Icon tile inside admin tool cards (brand primary tints). */
export function adminToolCardIconWrapperSx(theme: Theme, displayPrimaryColor: string): SxProps<Theme> {
  const p = displayPrimaryColor
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: alpha(p, 0.08),
    color: p,
    borderRadius: '14px',
    width: '64px',
    height: '64px',
    marginRight: '20px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    border: `1px solid ${alpha(p, 0.1)}`,
    '&:hover': {
      transform: 'scale(1.05)',
      backgroundColor: alpha(p, 0.12),
      boxShadow: `0 4px 12px 0 ${alpha(p, 0.2)}`,
    },
  } as SxProps<Theme>
}

/** Endpoint testing / danger-styled admin tool card. */
export function adminToolDangerCardChromeSx(theme: Theme): SxProps<Theme> {
  const e = theme.palette.error
  return {
    '&::before': {
      ...adminToolTopBar,
      background: `linear-gradient(135deg, ${e.dark} 0%, ${e.main} 100%)`,
    },
    '&:hover': {
      transform: 'translateY(-6px)',
      boxShadow: `0 12px 28px 0 ${alpha(e.main, 0.15)}`,
      borderColor: alpha(e.main, 0.2),
      '&::before': { transform: 'scaleX(1)' },
    },
  }
}

export function adminToolDangerCardFullSx(theme: Theme): SxProps<Theme> {
  return {
    ...adminToolCardBaseSx(theme),
    ...adminToolDangerCardChromeSx(theme),
  } as SxProps<Theme>
}

export function adminToolDangerIconWrapperSx(theme: Theme): SxProps<Theme> {
  const e = theme.palette.error.main
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: alpha(e, 0.08),
    color: e,
    borderRadius: '14px',
    width: '64px',
    height: '64px',
    marginRight: '20px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    border: `1px solid ${alpha(e, 0.1)}`,
    '&:hover': {
      transform: 'scale(1.05)',
      backgroundColor: alpha(e, 0.12),
      boxShadow: `0 4px 12px 0 ${alpha(e, 0.2)}`,
    },
  } as SxProps<Theme>
}

/** Disabled “Create room” style contained buttons (theme-aware). */
export function disabledContainedActionButtonSx(theme: Theme): SxProps<Theme> {
  const bg = theme.palette.action.disabledBackground
  const fg = theme.palette.text.disabled
  return {
    background: bg,
    color: fg,
    border: 'none',
    borderRadius: '8px',
    fontWeight: 500,
    textTransform: 'none',
    minHeight: '36px',
    boxShadow: 'none',
    '&:disabled': {
      background: bg,
      color: fg,
      boxShadow: 'none',
    },
  }
}

/** Primary gradient CTA (brand + theme primary ramp). */
export function primaryGradientContainedButtonSx(
  theme: Theme,
  displayPrimaryColor: string,
  companyDisabled: boolean
): SxProps<Theme> {
  if (companyDisabled) return disabledContainedActionButtonSx(theme)
  const p = displayPrimaryColor
  return {
    background: `linear-gradient(135deg, ${p} 0%, ${theme.palette.primary.light} 100%)`,
    color: theme.palette.primary.contrastText,
    border: 'none',
    borderRadius: '8px',
    fontWeight: 500,
    textTransform: 'none',
    minHeight: '36px',
    boxShadow: `0 4px 14px 0 ${alpha(p, 0.3)}`,
    '&:hover': {
      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${p} 100%)`,
      boxShadow: `0 10px 25px 0 ${alpha(p, 0.4)}`,
    },
    '&:disabled': {
      background: theme.palette.action.disabledBackground,
      color: theme.palette.text.disabled,
      boxShadow: 'none',
    },
  }
}

/** Outlined button using dynamic brand color + theme primary.dark on hover. */
export function outlinedBrandButtonSx(theme: Theme, displayPrimaryColor: string): SxProps<Theme> {
  const p = displayPrimaryColor
  return {
    color: p,
    borderColor: p,
    '&:hover': {
      backgroundColor: alpha(p, 0.08),
      borderColor: theme.palette.primary.dark,
      color: theme.palette.primary.dark,
    },
  }
}

export type SubscriptionChipTone = 'elevated' | 'active' | 'trialing' | 'past_due' | 'canceled' | 'neutral'

export function subscriptionStatusChipSx(theme: Theme, tone: SubscriptionChipTone): SxProps<Theme> {
  const pick = (main: string) => ({
    backgroundColor: alpha(main, 0.2),
    color: main,
    border: '1px solid',
    borderColor: alpha(main, 0.3),
  })
  switch (tone) {
    case 'elevated':
      return pick(theme.palette.secondary.main)
    case 'active':
      return pick(theme.palette.success.main)
    case 'trialing':
      return pick(theme.palette.info.main)
    case 'past_due':
      return pick(theme.palette.warning.main)
    case 'canceled':
      return pick(theme.palette.error.main)
    default:
      return pick(theme.palette.text.secondary)
  }
}

export function subscriptionChipToneFromState(
  serverVerifiedElevated: boolean,
  subscriptionStatus: string
): SubscriptionChipTone {
  if (serverVerifiedElevated) return 'elevated'
  if (subscriptionStatus === 'active') return 'active'
  if (subscriptionStatus === 'trialing') return 'trialing'
  if (subscriptionStatus === 'past_due') return 'past_due'
  if (subscriptionStatus === 'canceled') return 'canceled'
  return 'neutral'
}
